<template>
  <section class="hero">
    <div class="container">
      <span class="tag">{{ t('hero.tag') }}</span>

      <h1 class="serif" v-html="t('hero.title')"></h1>

      <p class="lead" v-html="t('hero.lead')"></p>

      <a href="#tool" class="hero-cta">
        <span>{{ t('hero.cta') }}</span>
        <span class="arrow" aria-hidden="true">&rarr;</span>
      </a>

      <aside class="hero-aside">
        <div class="hero-aside-accent"></div>
        <p class="quote-text" v-html="t('hero.quote')"></p>
        <cite v-html="t('hero.cite')"></cite>
      </aside>
    </div>
  </section>
</template>

<script setup>
import { useLocale } from '../composables/useLocale'

const { t } = useLocale()
</script>

<style scoped>
.hero { padding: 120px 0 80px; position: relative; }
.hero .container { position: relative; }
.hero .tag {
  display: inline-block;
  background: var(--ink);
  color: var(--bg);
  padding: 6px 16px;
  font-weight: 700;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-bottom: 32px;
  border-radius: 999px;
}
.hero h1 {
  font-size: clamp(56px, 9vw, 110px);
  font-weight: 800;
  line-height: 0.98;
  letter-spacing: -0.035em;
  margin-bottom: 36px;
  max-width: 22ch;
}
.hero h1 :deep(.hero-top) {
  display: inline-block;
  position: relative;
  z-index: 1;
}
.hero h1 :deep(.accent) {
  background: var(--yellow);
  padding: 0 14px;
  margin: 0 -4px;
  display: inline-block;
  border-radius: 8px;
  font-style: italic;
  font-weight: 700;
  transform: rotate(-1deg);
}
.hero .lead {
  font-size: clamp(19px, 2.2vw, 24px);
  max-width: 54ch;
  margin-bottom: 44px;
  color: var(--ink-soft);
  line-height: 1.55;
}
.hero .lead :deep(strong) { color: var(--ink); }
.hero-cta {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: var(--blue);
  color: #fff;
  text-decoration: none;
  font-weight: 700;
  padding: 18px 32px;
  font-size: 17px;
  border: none;
  border-radius: 999px;
  transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
  box-shadow: var(--shadow-soft);
}
.hero-cta:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-hover);
  background: var(--blue-dark);
}
.hero-cta .arrow {
  display: inline-block;
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}
.hero-cta:hover .arrow {
  transform: translateX(6px);
}

.hero-aside {
  position: absolute;
  top: 64px;
  right: max(-160px, calc((1120px - 100vw) / 2));
  width: 320px;
  background: var(--bg-card);
  border: 1px solid var(--line-soft);
  border-radius: 20px;
  padding: 28px 26px 24px;
  box-shadow: var(--shadow-soft);
  display: none;
  opacity: 0;
  animation: fadeUp 0.8s 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}
@media (min-width: 1100px) {
  .hero-aside {
    display: block;
  }
}
.hero-aside-accent {
  position: absolute;
  top: -1px;
  left: 30px;
  width: 56px;
  height: 5px;
  background: var(--yellow);
  border-radius: 0 0 5px 5px;
}
.hero-aside .quote-text {
  font-family: 'Fraunces', serif;
  font-style: italic;
  font-weight: 500;
  font-size: clamp(18px, 1.5vw, 21px);
  line-height: 1.35;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin: 0 0 22px 0;
}
.hero-aside cite {
  display: block;
  padding-top: 18px;
  border-top: 1px solid var(--line-soft);
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-style: normal;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: 0;
}
.hero-aside :deep(.cite-meta) {
  display: block;
  font-weight: 400;
  color: var(--ink-muted);
  font-size: 12px;
  margin-top: 2px;
}

.hero .tag,
.hero h1,
.hero .lead,
.hero-cta {
  opacity: 0;
  animation: fadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}
.hero .tag { animation-delay: 0.05s; }
.hero h1 { animation-delay: 0.15s; }
.hero .lead { animation-delay: 0.35s; }
.hero-cta { animation-delay: 0.5s; }

@media (max-width: 768px) {
  .hero { padding: 64px 0 48px; }
}
</style>
